import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import BackToTopButton from '../../components/feature/BackToTopButton';

const ContactoPage = () => {
  useEffect(() => {
    document.title = 'Contacto - PLADUREX | Presupuesto de Pladur en Mataró';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en Mataró. Presupuesto sin compromiso ☎ 629 501 941 | WhatsApp disponible');
    }

    // Add ContactPage Schema.org
    const existingContactSchema = document.querySelector('script[data-schema="contactpage"]');
    if (existingContactSchema) existingContactSchema.remove();
    
    const contactSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/contacto#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/contacto`,
      "name": "Contacto - PLADUREX | Presupuesto de Pladur en Mataró",
      "description": "Contacta con PLADUREX para tu proyecto de pladur en Mataró. Presupuesto sin compromiso ☎ 629 501 941 | WhatsApp disponible",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      }
    };
    
    const contactScript = document.createElement('script');
    contactScript.type = 'application/ld+json';
    contactScript.setAttribute('data-schema', 'contactpage');
    contactScript.textContent = JSON.stringify(contactSchema);
    document.head.appendChild(contactScript);

    // Add BreadcrumbList Schema.org
    const existingSchema = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingSchema) existingSchema.remove();
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": import.meta.env.VITE_SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contacto",
          "item": `${import.meta.env.VITE_SITE_URL}/contacto`
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'breadcrumb');
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (schemaScript) schemaScript.remove();
      const contactSchemaScript = document.querySelector('script[data-schema="contactpage"]');
      if (contactSchemaScript) contactSchemaScript.remove();
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    projectType: '',
    area: '',
    neighborhood: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6248io0h9ov63emh19g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          projectType: '',
          area: '',
          neighborhood: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Tabiques de pladur',
    'Trasdosados',
    'Falsos techos',
    'Aislamiento acústico',
    'Aislamiento térmico',
    'Pladur antihumedad',
    'Reforma completa',
    'Otro'
  ];

  const neighborhoods = [
    'Centre',
    'Eixample',
    'Cerdanyola',
    'Rocafonda',
    'El Palau',
    'Vista Alegre',
    'Cirera',
    'Peramàs',
    'Pla d\'en Boet',
    'L\'Havana',
    'Otro'
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F3F3F3]">
      <Header />
      <WhatsAppButton />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF007A]/10 via-[#0B0B0F] to-[#0B0B0F]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 break-words">
              <strong>Contacta con nosotros</strong>
              <span className="block mt-2 text-[#FF007A]">Presupuesto sin compromiso</span>
            </h1>
            <p className="text-lg text-[#F3F3F3]/90 max-w-3xl mx-auto leading-relaxed break-words">
              Cuéntanos tu proyecto de <strong>instalación de pladur en Mataró</strong> y te preparamos un presupuesto personalizado. Consulta nuestros <Link to="/servicios" className="text-[#FF007A] hover:underline">servicios disponibles</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 break-words">Información de <span className="text-[#FF007A]">contacto</span></h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#FF007A]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1 break-words">Teléfono</h3>
                    <a href="tel:+34629501941" className="text-[#F3F3F3]/70 hover:text-[#FF007A] transition-colors duration-200 break-all">
                      +34 629 501 941
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl text-[#FF007A]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1 break-words">WhatsApp</h3>
                    <a 
                      href="https://wa.me/34629501941" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#F3F3F3]/70 hover:text-[#FF007A] transition-colors duration-200 break-words"
                    >
                      Enviar mensaje
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-[#FF007A]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1 break-words">Área de servicio</h3>
                    <p className="text-[#F3F3F3]/70 break-words">Mataró, Maresme y Barcelona</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-xl text-[#FF007A]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1 break-words">Horario</h3>
                    <p className="text-[#F3F3F3]/70 break-words">Consultar</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#12121A] rounded-xl p-6 border border-[#FF007A]/10">
                <h3 className="text-lg sm:text-xl font-semibold mb-4 break-words">¿Por qué elegirnos?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-[#F3F3F3]/70 text-sm break-words">Presupuesto detallado sin compromiso</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-[#F3F3F3]/70 text-sm break-words">Visita y medición gratuita en Mataró</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-[#F3F3F3]/70 text-sm break-words">Obra limpia y rápida</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-[#F3F3F3]/70 text-sm break-words">Acabados profesionales listos para pintar</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-[#F3F3F3]/70 text-sm break-words">Factura y garantía</span>
                  </li>
                </ul>
                <p className="text-sm text-[#F3F3F3]/60 mt-4 break-words">
                  <Link to="/empresa" className="text-[#FF007A] hover:underline">Conoce más sobre nuestra empresa</Link> y nuestra forma de trabajar. ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-[#FF007A] hover:underline">preguntas frecuentes</Link>.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#12121A] rounded-xl p-6 sm:p-8 border border-[#FF007A]/10">
                <h3 className="text-xl sm:text-2xl font-bold mb-6 break-words">Solicita tu <span className="text-[#FF007A]">presupuesto</span></h3>
                
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">Nombre *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">Teléfono *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200"
                        placeholder="Tu teléfono"
                      />
                    </div>

                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium mb-2">Tipo de proyecto * <Link to="/servicios" className="text-[#FF007A] text-xs hover:underline">(ver servicios)</Link></label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200 cursor-pointer"
                      >
                        <option value="">Selecciona un tipo</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="area" className="block text-sm font-medium mb-2">Metros Cuadrados Aproximados</label>
                      <input
                        type="text"
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200"
                        placeholder="Ej: 20 m²"
                      />
                    </div>

                    <div>
                      <label htmlFor="neighborhood" className="block text-sm font-medium mb-2">Barrio / localidad</label>
                      <select
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200 cursor-pointer"
                      >
                        <option value="">Selecciona una zona</option>
                        {neighborhoods.map((neighborhood) => (
                          <option key={neighborhood} value={neighborhood}>{neighborhood}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">Mensaje</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-[#0B0B0F] border border-[#FF007A]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF007A] transition-colors duration-200 resize-none"
                        placeholder="Cuéntanos más sobre tu proyecto..."
                      ></textarea>
                      <p className="text-xs text-[#F3F3F3]/50 mt-1">Máximo 500 caracteres</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-4 bg-[#FF007A] text-white rounded-lg font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-400 break-words">¡Mensaje enviado correctamente! Te contactaremos pronto.</p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-sm text-red-400 break-words">Error al enviar el mensaje. Por favor, inténtalo de nuevo o llámanos.</p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 break-words">
              Zonas de <span className="text-[#FF007A]">servicio</span>
            </h2>
            <p className="text-base sm:text-lg text-[#F3F3F3]/70 mb-8 break-words">
              Realizamos <Link to="/servicios" className="text-[#FF007A] hover:underline">instalaciones de pladur</Link> en Mataró y toda la comarca del Maresme
            </p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-4xl mx-auto">
              {['Mataró Centre', 'Eixample', 'Cerdanyola', 'Rocafonda', 'El Palau', 'Vista Alegre', 'Cirera', 'Argentona', 'Cabrera de Mar', 'Vilassar de Mar', 'Premià de Mar', 'Arenys', 'Barcelona'].map((zone, index) => (
                <span key={index} className="px-3 sm:px-4 py-2 bg-[#0B0B0F] rounded-full text-xs sm:text-sm border border-[#FF007A]/20 break-words">
                  {zone}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BackToTopButton />
    </div>
  );
};

export default ContactoPage;
